
import {defineComponent, onMounted, onUnmounted, ref, watch, watchEffect} from "vue";
import {useStore} from 'vuex';
import {checkIfServiceIsRunning, toggleLoadingAnimation} from './../Composables/api';
import {callPaq, calculateProgressBarData, navigateToStepWithRouterLink, updateWindowLocation} from './../Composables/form';
import {stringToBoolean} from './../Composables/functions';
import {ApiActions, ApiGetters, FormActions, FormGetters} from './../Store/index';
import {Labels, Settings} from './../Store/types';
import {ElProgress} from "element-plus";

import FormComponent from "./FormComponent.vue";
import ModalComponent from "./Form/ModalComponent.vue";

export default defineComponent({
    name: "MainComponent",

    components: {
      FormComponent,
      ModalComponent,
      ElProgress
    },

    props: {
      serverHost: String,
      locationHref: String,
      labels: {
        type: Object as () => Labels,
        default: () => ({})
      },
      icons: Object,
      settings: {
        type: Object as () => Settings,
        default: () => ({})
      },
      images: Object
    },

    setup(props) {
      const store = useStore();

      const serverHost = props.settings?.serverHost ?? "",
          apiUrl = props.settings?.api?.url ?? "",
          serviceNotRunningTitle = props.labels?.serviceNotRunningTitle ?? "",
          serviceNotRunningMessage = props.labels?.serviceNotRunningMessage ?? "",
          isIntensiveUsedApiTitle = props.settings?.intensiveUseTitle ?? "",
          isIntensiveUsedApiMessage = props.settings?.intensiveUseBody ?? "",
          isStickyProgressBar = ref(false),
          formElement = ref<HTMLElement | null>(null),
          progressBarElementPosition = ref<number | null>(null);

      let showResultPage = ref(false),
          progressBarData = ref(0),
          isVisibleForm = ref(true),
          isIntensiveUsedApi = ref(false);

      store.dispatch(`form/${FormActions.SET_IS_VISIBLE_FORM}`, stringToBoolean(isVisibleForm.value));
      store.dispatch(`form/${FormActions.SET_LABELS}`, props.labels);
      store.dispatch(`form/${FormActions.SET_ICONS}`, props.icons);
      store.dispatch(`form/${FormActions.SET_SETTINGS}`, props.settings);
      store.dispatch(`form/${FormActions.SET_IMAGES}`, props.images);
      store.dispatch(`form/${FormActions.UPDATE_DATA}`, {
        field: 'hasContentElements', key: 'value', value: props.settings ? props.settings.hasContentElements : false
      });
      store.dispatch(`form/${FormActions.UPDATE_DATA}`, {
        field: 'hiddenContentQuerySelector', key: 'value', value: props.settings ? props.settings.hiddenContentQuerySelector : ''
      });
      store.dispatch(`api/${ApiActions.SET_SERVER_HOST}`, serverHost);
      store.dispatch(`api/${ApiActions.SET_URL}`, apiUrl);
      store.dispatch(`form/${FormActions.UPDATE_FUNNEL_STEP}`, {
        key: 'properties',
        value: {
          ...store.getters[`form/${FormGetters.GET_FUNNEL_STEP}`].properties,
          ...{
            stepProcessed: 'start',
            stepProcessedNum: 1,
            stepURL: store.getters[`form/${FormGetters.GET_LOCATION_WITH_HASH}`],
            referrerURL: store.getters[`form/${FormGetters.GET_LOCATION}`].oldUrl
          }
        },
      });

      // Function to calculate progress bar data
      const calProgressBarData = () => {
        // Calculate progress bar data
        progressBarData.value = calculateProgressBarData(store);
      };

      // Set location href in the form store and update window location.
      const setLocationHref = () => {
        // Dispatch an action to set the location href in the form store
        store.dispatch(`form/${FormActions.UPDATE_LOCATION}`, {
          key: 'href',
          value: props.locationHref ? props.locationHref : window.location.href
        });
        store.dispatch(`form/${FormActions.UPDATE_LOCATION}`, {
          key: 'oldUrl',
          value: store.getters[`form/${FormGetters.GET_LOCATION_WITH_HASH}`]
        });

        // Build the new URL by combining the stored location href with the router link
        // Update the window location href
        updateWindowLocation(store);
      };

      const handleLocationChange = async () => {
        const formIsValid = store.getters[`form/${FormGetters.GET_IS_VALID}`];

        if (
            (window.location.href.includes('#/start') && showResultPage.value) ||
            (window.location.href.includes('#/result') && !formIsValid)
        ) {
          // Update showResultPage.value to false
          showResultPage.value = false;

          await navigateToStepWithRouterLink(store, 1, 'start');
        }

        if (window.location.href.includes('#/result') && formIsValid && !showResultPage.value) {
          // Update showResultPage.value to true
          showResultPage.value = true;

          await navigateToStepWithRouterLink(store, 2, 'result');
        }

        // Update the store with the new value
        await store.dispatch(`form/${FormActions.SET_SHOW_RESULT_PAGE}`, showResultPage.value);
      };

      // Recursive function to check if the service is running
      const checkServiceStatus = async () => {
        const serviceRunning = await checkIfServiceIsRunning(serverHost);
        isVisibleForm.value = serviceRunning.value;

        // Update the store with the new value
        await store.dispatch(`form/${FormActions.SET_IS_VISIBLE_FORM}`, stringToBoolean(isVisibleForm.value));
      };

      // Watch for changes in isCar
      watchEffect(() => {
        showResultPage.value = store.getters[`form/${FormGetters.GET_SHOW_RESULT_PAGE}`];
        isVisibleForm.value = store.getters[`form/${FormGetters.GET_IS_VISIBLE_FORM}`];
        isIntensiveUsedApi.value = store.getters[`api/${ApiGetters.GET_IS_INTENSIVE_USED_API}`];
      });

      // Use watch to react to changes in the inputs property
      watch(() => store.getters[`form/${FormGetters.GET_INPUTS}`],
          () => {
            calProgressBarData();
          },
          { deep: true } // Set deep to true to watch nested properties
      );

      // Watch for changes in isVisibleForm
      watch(isVisibleForm, () => {
        if (!isVisibleForm.value) {
          // If service is not running, start the recursive check
          checkServiceStatus();
          return;
        }
      });

      watch(
          () => store.getters[`api/${ApiGetters.GET_ON_GOING_REQUEST}`],
          async (newVal, oldVal) => {
              if (newVal > 0 && oldVal === 0) {
                  await toggleLoadingAnimation(true);
              } else if (newVal === 0 && oldVal > 0) {
                  await toggleLoadingAnimation(false);
              }
          },
          { immediate: true }
      );

      // Function to update isStickyProgressBar based on scroll position
      const updateStickyStateForProgressBar = () => {
        if (progressBarElementPosition.value !== null && formElement.value) {
          const elementRect = formElement.value.getBoundingClientRect();
          const elementBottom = elementRect.bottom + window.scrollY - 100;

          // Check if the scroll position is between the top and bottom of the element
          isStickyProgressBar.value = window.scrollY > progressBarElementPosition.value && window.scrollY < elementBottom;
        }
      };

      // // Get the element and calculate its position when the component is mounted
      onMounted(() => {
        console.log("Shorteval: " + store.state.version);

        formElement.value = document.querySelector('.shorteval-form-component');

        if (formElement.value) {
          progressBarElementPosition.value = formElement.value.getBoundingClientRect().top - 100 + window.scrollY;

          // Attach the scroll event listener
          window.addEventListener('scroll', updateStickyStateForProgressBar);
          window.addEventListener('hashchange', handleLocationChange);

          // Initial update
          updateStickyStateForProgressBar();
        }
      });

      // Use onUnmounted to remove event listener when component is unmounted
      onUnmounted(() => {
        window.removeEventListener('scroll', updateStickyStateForProgressBar);
        window.removeEventListener('hashchange', handleLocationChange);
      });

      // Set location href
      setLocationHref();

      // Initial call for progress bar data
      calProgressBarData();

      // Initial check
      checkServiceStatus();

      callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'GFW', 'GFW-Start');

      return {
        isVisibleForm,
        serviceNotRunningTitle,
        serviceNotRunningMessage,
        isIntensiveUsedApiTitle,
        isIntensiveUsedApiMessage,
        showResultPage,
        progressBarData,
        isStickyProgressBar,
        isIntensiveUsedApi,
        store
      }
    }
  })
